import { computed, ref, watch } from 'vue';
import { useAuthStore } from '../modules/auth/store';
import { Products } from '../enums';
// import all the permissions available from enum, as well as permission checker from its utilities
import { UserPermission } from '../modules/permission/enum';
import { userCompanyPreference } from '@/composables/CompanyPreferenceComposable';
import { useLabelRenameStore } from '@/modules/label-rename/store';

export function useMenu() {
    const authStore = useAuthStore();
    const labelRenameStore = useLabelRenameStore();
    const {fetchCompanyPreference} = userCompanyPreference()

    // Added helper: returns true if user has any of the given permissions
    const hasAnyPermission = (permissions) => permissions.some((p) => authStore.hasPermission(p));

    const buildMenuItems = (companyPreferences = {}) => {
        const l = (defaultName) => labelRenameStore.getLabel(defaultName, defaultName);

        return [
            // company selection is only for the root users
            {
                // label: 'Selection',
                icon: 'pi pi-fw pi-building',
                items: [{ label: 'Company', icon: 'pi pi-fw pi-building', to: '/company' }],
                visible: authStore.hasPermission(UserPermission.READ_COMPANY) && authStore.isGodUser,
                product: Products.GENERIC,
            },
            {
                items: [{ label: `Add ${l('Debrief')}`, icon: 'pi pi-fw pi-car', to: { name: 'DebriefCreateFast' }, style: 'background-color: #801136' }],
                visible: authStore.hasPermission(UserPermission.WRITE_DEBRIEF),
                product: Products.AMS,
                companyPreference: 'DebriefCreateFast',
            },
            {
                // label: 'Favorites',
                icon: 'pi pi-fw pi-home',
                // show Favorites section if user can read metrics
                visible: hasAnyPermission([UserPermission.READ_METRIC]),
                items: [{ label: 'Dashboard', icon: 'pi pi-fw pi-database', visible: authStore.hasPermission(UserPermission.READ_METRIC) }],
                product: Products.PHA,
            },
            {
                items: [
                    {
                        label: 'Manage Forms',
                        icon: 'pi pi-fw pi-check-square',
                        // show Manage Forms if user can read group permission user
                        visible: hasAnyPermission([UserPermission.READ_GROUP_PERMISSION_USER]),
                        items: [{ label: 'Attributes', icon: 'pi pi-fw pi-bars', to: { name: 'Attribute' }, visible: authStore.hasPermission(UserPermission.READ_GROUP_PERMISSION_USER) }],
                    },
                ],
                product: Products.PHA,
            },
            {
                items: [
                    { label: l('Home'), icon: 'pi pi-fw pi-home', to: { name: 'Home' } },
                    {
                        label: 'Events',
                        icon: 'pi pi-fw pi-building',
                        companyPreference: 'events_module_enabled',
                        items: [
                            { label: 'View Events', icon: 'pi pi-fw pi-list', to: { name: 'Events' }, companyPreference: 'events_module_enabled' },
                            { label: 'Add New Event', icon: 'pi pi-fw pi-plus', to: { name: 'EventCreateUpdate' }, companyPreference: 'events_module_enabled' },
                        ],
                    },
                    {
                        label: l('Tasks'),
                        icon: 'pi pi-fw pi-align-left',
                        items: [
                            { label: `View ${l('Tasks')}`, icon: 'pi pi-fw pi-list', to: { name: 'Tasks' }, visible: authStore.hasPermission(UserPermission.READ_TASK) },
                            { label: `Add New ${l('Task')}`, icon: 'pi pi-fw pi-plus', to: { name: 'TaskForm' }, visible: authStore.hasPermission(UserPermission.WRITE_TASK) },
                            { label: 'Draft', icon: 'pi pi-fw pi-folder-open', to: { name: 'DraftTasks' }, visible: authStore.hasPermission(UserPermission.WRITE_TASK) },
                        ],
                    },
                    {
                        label: l('Report'),
                        icon: 'pi pi-fw pi-file',
                        items: [
                            { label: `${l('Task')} ${l('Report')}`, icon: 'pi pi-fw pi-list', to: { name: 'TaskReports' },      visible: authStore.hasPermission(UserPermission.READ_TASK_DYNAMIC_REPORT) },
                            { label: `${l('Debrief')} ${l('Report')}`, icon: 'pi pi-fw pi-list', to: { name: 'DebriefReports' }, visible: authStore.hasPermission(UserPermission.READ_DEBRIEF_DYNAMIC_REPORT) },
                        ],
                    },
                    {
                        label: 'Incident',
                        icon: 'pi pi-fw pi-bell',
                        visible: authStore.hasPermission(UserPermission.GLOBAL_TASK_FORCE) || authStore.isGodUser,
                        items: [
                            { label: 'View Incidents', icon: 'pi pi-fw pi-list', to: { name: 'Incidents' }, visible: authStore.hasPermission(UserPermission.READ_INCIDENT) },
                            { label: 'Add Incident', icon: 'pi pi-fw pi-plus', to: { name: 'IncidentCreateUpdate' }, visible: authStore.hasPermission(UserPermission.WRITE_INCIDENT) },
                        ],
                    },
                    {
                        label: l('Debrief'),
                        icon: 'pi pi-fw pi-check-square',
                        items: [
                            { label: `View ${l('Debriefs')}`, icon: 'pi pi-fw pi-list', to: { name: 'Debriefs' }, visible: authStore.hasPermission(UserPermission.READ_DEBRIEF) },
                            { label: `Add ${l('Debrief')}`, icon: 'pi pi-fw pi-plus', to: { name: 'DebriefCreateUpdate' }, visible: authStore.hasPermission(UserPermission.WRITE_DEBRIEF) },
                        ],
                    },
                    {
                        label: `View ${l('Learning Outcomes')}`,
                        icon: 'pi pi-fw pi-building-columns',
                        to: { name: 'LearningOutcomes' },
                        visible: (authStore.hasPermission(UserPermission.GLOBAL_TASK_FORCE) || authStore.isGodUser) && authStore.hasPermission(UserPermission.READ_LEARNING_OUTCOME),
                    },
                    {
                        label: l('Department'),
                        icon: 'pi pi-fw pi-building',
                        items: [{ label: `Manage ${l('Department')}`, icon: 'pi pi-fw pi-list', to: { name: 'Department' }, visible: authStore.hasPermission(UserPermission.WRITE_DEPARTMENT) }],
                    },
                    {
                        label: 'Event Category',
                        icon: 'pi pi-fw pi-building',
                        companyPreference: 'events_module_enabled',
                        items: [{ label: 'Manage Event Category', icon: 'pi pi-fw pi-list', to: { name: 'EventCategory' }, companyPreference: 'events_module_enabled' }],
                    },
                    {
                        label: 'Source',
                        icon: 'pi pi-fw pi-table',
                        items: [{ label: 'Manage Sources', icon: 'pi pi-fw pi-list', to: { name: 'Source' }, visible: authStore.hasPermission(UserPermission.WRITE_SOURCE) }],
                    },
                    {
                        label: 'Themes',
                        icon: 'pi pi-fw pi-th-large',
                        items: [{ label: 'Manage Themes', icon: 'pi pi-fw pi-list', to: { name: 'Theme' }, visible: authStore.hasPermission(UserPermission.WRITE_THEME) }],
                    },
                    {
                        label: 'Appliances',
                        icon: 'pi pi-fw pi-code',
                        items: [{ label: 'Manage Appliances', icon: 'pi pi-fw pi-list', to: { name: 'Appliance' }, visible: authStore.hasPermission(UserPermission.READ_CALLSIGN) }],
                    },
                    {
                        label: 'Checklist',
                        icon: 'pi pi-fw pi-list',
                        // show Checklist group if user can read checklist or station
                        visible: hasAnyPermission([UserPermission.READ_CHECKLIST, UserPermission.READ_STATION]),
                        items: [
                            { label: 'Manage Checklist', icon: 'pi pi-fw pi-list', to: { name: 'Checklist' }, visible: authStore.hasPermission(UserPermission.READ_CHECKLIST) },
                            { label: 'Station', icon: 'pi pi-building', to: { name: 'station' }, visible: authStore.hasPermission(UserPermission.READ_STATION) }
                        ],
                    },
                ],
                product: Products.AMS,
            },
            {
                items: [
                    {
                        label: 'Users',
                        icon: 'pi pi-fw pi-users',
                        items: [{ label: 'Manage Users', icon: 'pi pi-fw pi-list', to: { name: 'Users' }, visible: authStore.hasPermission(UserPermission.READ_USERS) }],
                    },
                    {
                        label: 'Settings',
                        icon: 'pi pi-fw pi-cog',
                        items: [
                            {
                                label: 'License',
                                icon: 'pi pi-fw pi-credit-card',
                                to: { name: 'License' },
                                visible: authStore.isGodUser || (authStore.hasPermission(UserPermission.READ_LICENCE) && authStore.hasPermission(UserPermission.READ_PRODUCT)),
                            },
                            { label: 'Permission', icon: 'pi pi-fw pi-key', to: { name: 'Permission' }, visible: authStore.isGodUser },
                            { label: 'Manage Groups', icon: 'pi pi-fw pi-list', to: { name: 'Groups' }, visible: authStore.hasPermission(UserPermission.READ_GROUPS) },
                            { label: 'Manage Group Membership', icon: 'pi pi-fw pi-users', to: { name: 'GroupMembership' }, visible: authStore.hasPermission(UserPermission.READ_PERMISSION_USER) },
                            {
                                label: 'Label Rename',
                                icon: 'pi pi-fw pi-pencil',
                                to: { name: 'LabelRename' },
                                // Backend returns an empty rename_labels array when the feature is disabled.
                                visible: authStore.isGodUser && companyPreferences?.label_rename_enabled === 1,
                            },
                        ],
                    },
                    { label: 'Help', icon: 'pi pi-fw pi-question', url: "https://ignis.freshdesk.com/support/home", target:"_blank", style: 'background-color: rgb(24,108,185'  },
                ],
                product: Products.GENERIC,
            },
        ];
    };

    const company_id = computed(() => {
        return authStore.getLoggedUserCompanyId;
    });

    watch(company_id, () => {
        menuSetup()
    });

    const filteredMenuItems = ref([])

    // Recursively drop items disabled by their companyPreference, then hide any
    // parent whose children have all become invisible. A parent is treated as a
    // pure container when it has no direct `to` / `url` of its own.
    function applyVisibilityRules(items, companyPreferences) {
        if (!Array.isArray(items)) return;
        for (const item of items) {
            if (!item.items) continue;
            item.items = item.items.filter((child) => {
                if (child.companyPreference) {
                    return companyPreferences?.[child.companyPreference] === 1;
                }
                return true;
            });
            applyVisibilityRules(item.items, companyPreferences);
            const isContainer = !item.to && !item.url;
            if (isContainer) {
                const anyChildVisible = item.items.some((child) => child.visible !== false);
                if (!anyChildVisible) {
                    item.visible = false;
                }
            }
        }
    }

    function menuSetup() {
        if (typeof company_id.value !== 'undefined' && company_id.value > 0) {
            fetchCompanyPreference(company_id.value).then(async (companyPreferences) => {
                await labelRenameStore.ensureLoaded(companyPreferences);
                const menuItems = buildMenuItems(companyPreferences);
                const topLevel = menuItems?.filter((menuItem) => {
                    let allowed = true;
                    if (menuItem.companyPreference) {
                        allowed = companyPreferences?.[menuItem.companyPreference] === 1;
                    }
                    return (menuItem.product === Products.GENERIC || menuItem.product === authStore.selectedProduct) && allowed;
                }) ?? [];
                applyVisibilityRules(topLevel, companyPreferences);
                filteredMenuItems.value = topLevel;
            })
        }
    }

    menuSetup()

    return {
        filteredMenuItems,
    };
}
